import LoadingScreen from "../../../utils/LoadingScreen";
import { KitchenPrint } from "./KitchenPrint";

const OrderTable = ({
  orderDetails,
  selectedDeliveryPerson,
  activeTab,
  handleKitchenPrint,
  reciptDetails,
  handleMarkDelivered,
  handleImageClick,
  loading = false,
}) => {
  const steps = [
    "Order Initiated",
    "Order Payment",
    "Order Dispatched",
    "Order Completed",
  ];

  const statusMap = {
    received: 1,
    paid: 2,   
    accepted: 2,
    prepared: 2,
    dispatched: 3,
    acknowledged: 3,
    "food is ready": 3,
    ready_for_takeaway: 4,
    ready_for_dinning: 4,
    delivered: 4,
    completed: 4,
    successful: 4,
  };

  const normalizedOrderType = (
    orderDetails?.orderType ||
    orderDetails?.order_type ||
    ""
  )
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "_");

  const isTableBasedOrder = [
    "dining",
    "dinning",
    "office_order",
    "car_order",
  ].includes(normalizedOrderType);

  const isRejected = orderDetails?.status === "rejected";

  const hasRealValue = (value) => {
    const v = String(value || "")
      .trim()
      .toLowerCase();

    return v && !["na", "n/a", "null", "undefined", "-"].includes(v);
  };

  const hasDeliveryPartnerDetails =
    hasRealValue(orderDetails?.delivery_person_name) ||
    hasRealValue(orderDetails?.delivery_person_number);

  const deliveryStatus = orderDetails?.delivery_partner_status
    ?.toLowerCase()
    ?.replace(/\s+/g, " "); // normalize spaces

  const orderStatus = orderDetails?.status
    ?.toLowerCase()
    ?.replace("-", "_")
    ?.trim();

  let finalStatus = orderStatus;

  if (
    deliveryStatus &&
    ["acknowledged", "food is ready", "delivery_picked"].includes(
      deliveryStatus,
    )
  ) {
    finalStatus = deliveryStatus;
  }
  const activeStep = statusMap[finalStatus] || 1;
  const totalSteps = steps.length - 1;
  const greenWidthPercent = ((activeStep - 1) / totalSteps) * 100;
  const isCompleted = [
    "completed",
    "delivered",
    "ready_for_takeaway",
    "ready_for_dinning",
  ].includes(finalStatus);

  const renderAddress = () => {
    try {
      const parsed =
        typeof orderDetails?.address_json === "string"
          ? JSON.parse(orderDetails.address_json || "{}")
          : orderDetails?.address_json || {};

      const v = parsed?.values || {};

      const parts = [
        v.address,
        v.building_name,
        v.landmark_area,
        v.house_number,
        v.tower_number,
        v.floor_number,
        v.city,
        v.state,
        v.in_pin_code,
      ].filter(Boolean);

      const finalAddress =
        parts.length > 0 ? parts.join(", ") : orderDetails?.address_line || "-";

      return (
        <p className="text-gray-700 leading-6 font-semibold mb-4">
          {finalAddress}
        </p>
      );
    } catch {
      return (
        <p className="text-gray-700 leading-6 font-semibold mb-4">
          {orderDetails?.address_line || "-"}
        </p>
      );
    }
  };

  const renderActionButton = () => {
    if (activeTab === "current") {
      return (
        <button
          className="bg-green-500 text-white px-4 py-2 rounded text-sm font-medium"
          onClick={() => KitchenPrint(reciptDetails, orderDetails, "")}
        >
          Kitchen Receipt
        </button>
      );
    }

    if (activeTab === "history") {
      if (isCompleted) {
        // History + completed/delivered → Download Receipt
        return (
          <button
            onClick={() => handleImageClick(orderDetails)}
            className="bg-blue-500 text-white px-4 py-2 rounded-lg text-xs whitespace-nowrap"
          >
            Download Receipt
          </button>
        );
      } else {
        // History + not yet completed → Mark as Delivered
        return (
          <button
            onClick={() => handleMarkDelivered(orderDetails)}
            className="bg-green-500 text-white px-4 py-2 rounded-lg text-xs whitespace-nowrap"
          >
            Mark as Delivered
          </button>
        );
      }
    }

    return null;
  };

  const parseCustomization = (rawCustomization) => {
    if (!rawCustomization) return null;

    try {
      let parsed = rawCustomization;

      // API kabhi JSON string aur kabhi double-stringified JSON bhej sakti hai.
      for (let attempt = 0; attempt < 2; attempt += 1) {
        if (typeof parsed !== "string") break;
        parsed = JSON.parse(parsed);
      }

      return parsed && typeof parsed === "object" ? parsed : null;
    } catch (error) {
      console.error("Customization parse error:", error, rawCustomization);
      return null;
    }
  };

  const formatLabel = (value) => {
    return String(value || "")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (character) => character.toUpperCase());
  };

  const formatEventDate = (value) => {
    if (!value) return "";

    const [year, month, day] = String(value).split("-");

    if (!year || !month || !day) return String(value);

    return `${day}/${month}/${year}`;
  };

  const formatEventTime = (value) => {
    if (!value) return "";

    const [hourValue, minuteValue = "00"] = String(value).split(":");
    let hour = Number(hourValue);

    if (Number.isNaN(hour)) return String(value);

    const period = hour >= 12 ? "PM" : "AM";
    hour = hour % 12 || 12;

    return `${hour}:${minuteValue} ${period}`;
  };

  const renderEventDetails = (event) => {
    if (!event || typeof event !== "object") return null;

    const hasEventData =
      event.location || event.eventDate || event.startTime || event.endTime;

    if (!hasEventData) return null;

    return (
      <div className="mt-2 rounded-md border border-pink-200 bg-pink-50 p-2">
        <p className="mb-1 text-[11px] font-semibold text-pink-700">
          Event Details
        </p>

        <div className="flex flex-wrap gap-1.5">
          {event.location && (
            <span className="rounded-full border border-pink-200 bg-white px-2 py-0.5 text-[10px] text-pink-700">
              Location: {event.location}
            </span>
          )}

          {event.eventDate && (
            <span className="rounded-full border border-pink-200 bg-white px-2 py-0.5 text-[10px] text-pink-700">
              Date: {formatEventDate(event.eventDate)}
            </span>
          )}

          {event.startTime && (
            <span className="rounded-full border border-pink-200 bg-white px-2 py-0.5 text-[10px] text-pink-700">
              Start: {formatEventTime(event.startTime)}
            </span>
          )}

          {event.endTime && (
            <span className="rounded-full border border-pink-200 bg-white px-2 py-0.5 text-[10px] text-pink-700">
              End: {formatEventTime(event.endTime)}
            </span>
          )}
        </div>
      </div>
    );
  };

  const renderSelectedItem = (selectedItem, itemIndex) => {
    if (!selectedItem || typeof selectedItem !== "object") return null;

    const itemName =
      selectedItem.itemName ||
      selectedItem.addonName ||
      selectedItem.productName ||
      selectedItem.name ||
      `Item ${itemIndex + 1}`;

    return (
      <li
        key={`${selectedItem.optionGroupItemId || selectedItem.itemId || selectedItem.addonId || itemIndex}-${itemIndex}`}
        className="flex flex-wrap items-center gap-x-2 gap-y-1 border-t border-blue-100 py-1.5 first:border-t-0"
      >
        <span className="text-[11px] font-medium text-gray-700">
          {itemName}
        </span>

        {selectedItem.quantityPerPerson != null && (
          <span className="rounded bg-green-50 px-1.5 py-0.5 text-[9px] text-green-700">
            {selectedItem.quantityPerPerson} per person
          </span>
        )}

        {selectedItem.fixedQuantity != null && (
          <span className="rounded bg-yellow-50 px-1.5 py-0.5 text-[9px] text-yellow-700">
            Qty: {selectedItem.fixedQuantity}
          </span>
        )}

        {selectedItem.selectedQuantity != null && (
          <span className="rounded bg-yellow-50 px-1.5 py-0.5 text-[9px] text-yellow-700">
            Qty: {selectedItem.selectedQuantity}
          </span>
        )}

        {Number(selectedItem.extraPrice || 0) > 0 && (
          <span className="rounded bg-orange-50 px-1.5 py-0.5 text-[9px] text-orange-700">
            +₹{Number(selectedItem.extraPrice).toFixed(2)}
          </span>
        )}

        {selectedItem.unitPrice != null && (
          <span className="rounded bg-orange-50 px-1.5 py-0.5 text-[9px] text-orange-700">
            ₹{Number(selectedItem.unitPrice || 0).toFixed(2)}
          </span>
        )}
      </li>
    );
  };

  
  const renderCompactSelectedItem = (selectedItem, itemIndex) => {
    if (!selectedItem || typeof selectedItem !== "object") return null;

    const itemName =
      selectedItem.itemName ||
      selectedItem.addonName ||
      selectedItem.productName ||
      selectedItem.name ||
      `Item ${itemIndex + 1}`;

    return (
      <span
        key={`compact-${
          selectedItem.optionGroupItemId ||
          selectedItem.itemId ||
          selectedItem.addonId ||
          itemIndex
        }-${itemIndex}`}
        className="inline-flex items-center gap-1 rounded-md bg-gray-50 px-2 py-1 text-[10px] text-gray-700"
      >
        <span className="font-medium">{itemName}</span>

        {selectedItem.quantityPerPerson != null && (
          <span className="rounded bg-green-100 px-1 py-[1px] text-[9px] text-green-700">
            {selectedItem.quantityPerPerson}/person
          </span>
        )}

        {selectedItem.fixedQuantity != null && (
          <span className="text-[9px] text-gray-500">
            ×{selectedItem.fixedQuantity}
          </span>
        )}

        {selectedItem.selectedQuantity != null && (
          <span className="text-[9px] text-gray-500">
            ×{selectedItem.selectedQuantity}
          </span>
        )}

        {Number(selectedItem.extraPrice || 0) > 0 && (
          <span className="text-[9px] font-medium text-orange-600">
            +₹{Number(selectedItem.extraPrice).toFixed(2)}
          </span>
        )}
      </span>
    );
  };

const renderGenericCustomization = (parsed) => {
  const ignoredKeys = new Set([
    "schemaversion",
    "cartkey",
    "selectedgroups",
    "selectedgroupsetmembers",
    "packageaddons",
    "event",

    // Hide technical fields
    "itemname",
    "itemid",
    "categoryid",
    "categorytype",
    "linetype",
    "categoryname",
    "taxpercentage",
    "taxpercent",
    "tax_percentage",
    "packageprice",
    "package_price",
    "variant",
    "variantid",
    "variantname",
  ]);

  const simpleEntries = Object.entries(parsed).filter(([key, value]) => {
    const normalizedKey = String(key)
      .replace(/[_\s-]/g, "")
      .toLowerCase();

    return (
      !ignoredKeys.has(String(key).toLowerCase()) &&
      !ignoredKeys.has(normalizedKey) &&
      value != null &&
      ["string", "number", "boolean"].includes(typeof value) &&
      String(value).trim() !== ""
    );
  });

  if (simpleEntries.length === 0) return null;

  return (
    <div className="mt-1.5 flex max-w-full flex-wrap items-center gap-1">
      {simpleEntries.map(([key, value]) => (
        <span
          key={`${key}-${String(value)}`}
          title={`${formatLabel(key)}: ${String(value)}`}
          className="inline-flex max-w-full items-center rounded-md border border-blue-200 bg-blue-50 px-2 py-[2px] text-[9px] leading-4 text-blue-700"
        >
          <span className="font-semibold">{formatLabel(key)}:</span>

          <span className="ml-1">{String(value)}</span>
        </span>
      ))}
    </div>
  );
};

  const renderCustomization = (item) => {
    const raw =
      item?.customization || item?.customizations || item?.customization_json;

    const parsed = parseCustomization(raw);

    if (!parsed) return null;

    const lineType = String(parsed.lineType || "").toUpperCase();
    const selectedGroups = Array.isArray(parsed.selectedGroups)
      ? parsed.selectedGroups
      : [];
    const packageAddons = Array.isArray(parsed.packageAddons)
      ? parsed.packageAddons
      : [];

    const groupSetEntries =
      parsed.selectedGroupSetMembers &&
      typeof parsed.selectedGroupSetMembers === "object"
        ? Object.entries(parsed.selectedGroupSetMembers)
        : [];

    if (lineType === "GLOBAL_ADDON") {
      const addonTotal =
        Number(parsed.selectedQuantity || 0) * Number(parsed.unitPrice || 0);

      return (
        <div className="mt-2 rounded-lg border border-orange-200 bg-orange-50 p-2">
          <div className="mb-1 text-[11px] font-semibold text-orange-700">
            Add-on Details
          </div>

          <div className="flex flex-wrap items-center gap-1.5">
            {parsed.selectedQuantity != null && (
              <span className="rounded-full border border-orange-200 bg-white px-2 py-0.5 text-[10px] text-orange-700">
                Qty: {parsed.selectedQuantity}
              </span>
            )}

            {parsed.unitPrice != null && (
              <span className="rounded-full border border-orange-200 bg-white px-2 py-0.5 text-[10px] text-orange-700">
                Unit: ₹{Number(parsed.unitPrice || 0).toFixed(2)}
              </span>
            )}

            {addonTotal > 0 && (
              <span className="rounded-full border border-orange-200 bg-white px-2 py-0.5 text-[10px] font-semibold text-orange-700">
                Total: ₹{addonTotal.toFixed(2)}
              </span>
            )}
          </div>
        </div>
      );
    }

    if (lineType === "PACKAGE" || parsed.packageId || parsed.packageName) {
      return (
        <div className="mt-2 rounded-lg border border-blue-200 bg-blue-50 p-2">
          <div className="mb-1 text-[11px] font-semibold text-blue-700">
            Package Customization
          </div>

          <div className="flex flex-wrap items-center gap-1.5">
            {parsed.people != null && (
              <span className="rounded-full border border-green-200 bg-green-50 px-2 py-0.5 text-[10px] text-green-700">
                People: {parsed.people}
              </span>
            )}

            {parsed.minimumPeople != null && (
              <span className="rounded-full border border-green-200 bg-green-50 px-2 py-0.5 text-[10px] text-green-700">
                Minimum: {parsed.minimumPeople}
              </span>
            )}

            {parsed.packageQuantity != null && (
              <span className="rounded-full border border-gray-200 bg-white px-2 py-0.5 text-[10px] text-gray-700">
                Package Qty: {parsed.packageQuantity}
              </span>
            )}
          </div>

          {selectedGroups.length > 0 && (
            <div className="mt-1.5 overflow-hidden rounded-md border border-blue-100 bg-white">
              {selectedGroups.map((group, groupIndex) => {
                const groupItems = Array.isArray(group?.items)
                  ? group.items
                  : [];

                return (
                  <div
                    key={`${
                      group?.packageOptionGroupId ||
                      group?.optionGroupId ||
                      groupIndex
                    }-${groupIndex}`}
                    className="border-b border-gray-100 px-2 py-1.5 last:border-b-0"
                  >
                    <div className="flex items-start gap-2">
                      {/* GROUP NAME */}
                      <div className="w-[105px] shrink-0 pt-0.5">
                        <p className="truncate text-[10px] font-semibold text-blue-700">
                          {group?.groupTitle || `Group ${groupIndex + 1}`}
                        </p>

                        <p className="mt-0.5 text-[8px] text-gray-400">
                          {groupItems.length} selected
                        </p>
                      </div>

                      {/* ITEMS */}
                      <div className="flex min-w-0 flex-1 flex-wrap gap-1">
                        {groupItems.map((selectedItem, itemIndex) =>
                          renderCompactSelectedItem(selectedItem, itemIndex),
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {groupSetEntries.length > 0 && (
            <div className="mt-2 rounded-md border border-indigo-200 bg-indigo-50 p-2">
              <p className="mb-1 text-[11px] font-semibold text-indigo-700">
                Group Set Selection
              </p>

              <div className="space-y-1">
                {groupSetEntries.map(([groupName, members]) => {
                  const memberList = Array.isArray(members)
                    ? members
                    : members && typeof members === "object"
                      ? Object.values(members)
                      : [members];

                  return (
                    <div key={groupName}>
                      <p className="text-[10px] font-medium text-indigo-700">
                        {formatLabel(groupName)}
                      </p>

                      <ul>
                        {memberList.map((member, memberIndex) =>
                          typeof member === "object" ? (
                            renderSelectedItem(member, memberIndex)
                          ) : (
                            <li
                              key={`${groupName}-${memberIndex}`}
                              className="text-[10px] text-gray-700"
                            >
                              {String(member)}
                            </li>
                          ),
                        )}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {packageAddons.length > 0 && (
            <div className="mt-2 rounded-md border border-orange-200 bg-orange-50 p-2">
              <p className="mb-1 text-[11px] font-semibold text-orange-700">
                Package Add-ons
              </p>

              <ul>
                {packageAddons.map((addon, addonIndex) =>
                  renderSelectedItem(addon, addonIndex),
                )}
              </ul>
            </div>
          )}
        </div>
      );
    }

   return renderGenericCustomization(parsed);
  };

  const getOrderEventDetails = () => {
    const items = Array.isArray(orderDetails?.items) ? orderDetails.items : [];

    for (const item of items) {
      const raw =
        item?.customization || item?.customizations || item?.customization_json;

      const parsed = parseCustomization(raw);
      const event = parsed?.event;

      if (
        event &&
        typeof event === "object" &&
        (event.location || event.eventDate || event.startTime || event.endTime)
      ) {
        return event;
      }
    }

    return null;
  };

  const orderEventDetails = getOrderEventDetails();

  const hasOrderData =
    orderDetails &&
    typeof orderDetails === "object" &&
    Object.keys(orderDetails).length > 0;

  const isOrderLoading = loading || !hasOrderData;

  if (isOrderLoading) {
    return (
      <div className="w-full min-h-[420px] bg-blue-50 flex items-center justify-center montserrat-font-1">
        <LoadingScreen text="Loading order details..." />
      </div>
    );
  }

  return (
    <div className="relative z-0 isolate w-full h-full flex flex-col overflow-hidden montserrat-font-1">
      {/* Wrapper stretches to full table row width */}
      <div className="w-full bg-blue-50 px-4 py-4 box-border montserrat-font-1">
        {/* PROGRESS BAR ROW */}
        {!isRejected && (
          <div className="bg-white rounded-lg px-6 py-4 w-full">
            <div className="flex items-center w-full gap-4">
              {/* Steps — takes all available space */}
              <div className="relative flex flex-1 items-center justify-between ">
                <div
                  className="absolute h-[2px] bg-gray-200"
                  style={{ top: "12px", left: "40px", right: "50px" }}
                />

                <div
                  className="absolute h-[2px] bg-green-500 transition-all duration-500"
                  style={{
                    top: "12px",
                    left: "40px",
                    width: `calc((100% - 94px) * ${greenWidthPercent} / 100)`,
                  }}
                />
                {steps.map((step, i) => (
                  <div key={i} className="flex flex-col items-center z-10">
                    <div
                      className={`w-6 h-6 rounded-full text-white text-xs flex items-center justify-center ${
                        i + 1 <= activeStep ? "bg-green-500" : "bg-gray-300"
                      }`}
                    >
                      {i + 1}
                    </div>
                    <p className="mt-2 text-xs whitespace-nowrap">{step}</p>
                  </div>
                ))}
              </div>

              {/* Button — fixed, does not shrink */}
              <div className="flex-shrink-0">{renderActionButton()}</div>
            </div>
          </div>
        )}

        {/* DELIVERY PARTNER DETAILS */}
        {!isTableBasedOrder && !isRejected && hasDeliveryPartnerDetails && (
          <div className="bg-white rounded-lg p-4 mt-4 w-full">
            <h4 className="font-semibold mb-2">Delivery Partner Details</h4>

            {hasRealValue(orderDetails?.delivery_person_name) && (
              <p className="text-sm">
                Name: {orderDetails.delivery_person_name}
              </p>
            )}

            {hasRealValue(orderDetails?.delivery_person_number) && (
              <p className="text-sm">
                Contact: {orderDetails.delivery_person_number}
              </p>
            )}
          </div>
        )}
      </div>

      {/* BOTTOM GRID — 2/3 left, 1/3 right */}
      <div className="relative z-0 flex-1 min-h-0 grid grid-cols-3 gap-4 bg-blue-50 p-4 overflow-hidden montserrat-font-1">
        {/* LEFT — Items table + Bill Summary */}
        <div className="col-span-2 bg-white rounded-lg p-4 flex flex-col min-h-0 overflow-hidden montserrat-font-1">
      <div className="relative z-0 flex-1 min-h-0 overflow-y-auto">
           <table
  className="relative z-0 w-full text-sm mb-4 montserrat-font-1"
  style={{ tableLayout: "fixed" }}
>
              <colgroup>
                <col style={{ width: "8%" }} />
                <col style={{ width: "44%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "18%" }} />
                <col style={{ width: "18%" }} />
              </colgroup>
            <thead className="sticky top-0 z-[1] bg-white text-gray-500">
                <tr>
                  <th className="text-left pb-2">S.No.</th>
                  <th className="text-left pb-2">Item Name</th>
                  <th className="text-right pb-2">Qty</th>
                  <th className="text-right pb-2">Unit Price</th>
                  <th className="text-right pb-2">Price</th>
                </tr>
              </thead>
              <tbody>
                {orderDetails?.items?.map((item, i) => (
                  <tr key={i} className="border-t border-b">
                    <td className="py-2">{i + 1}</td>
                    <td className="py-2">
                      <div className="flex flex-col">
                        <span className="truncate">
                          {item?.item_name || item?.product_id}
                        </span>

                        {renderCustomization(item)}
                      </div>
                    </td>
                    <td className="py-2 text-right">{item?.quantity}</td>
                    <td className="py-2 text-right">₹ {item?.item_price}</td>
                    <td className="py-2 text-right">₹ {item?.total_price}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

{/* KITCHEN NOTE */}

 {/* KITCHEN NOTE */}
{orderDetails?.kitchen_note &&
  String(orderDetails.kitchen_note).trim() && (
    <div
      className="mb-4 rounded-lg bg-gray-50 px-5 py-4"
      style={{
        border: "2px dotted #9ca3af",
      }}
    >
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-900">
        Kitchen Note
      </p>

      <p className="whitespace-pre-wrap text-sm leading-6 text-gray-700">
        {orderDetails.kitchen_note}
      </p>
    </div>
  )}

          {/* BILL SUMMARY */}
          <div className="text-sm space-y-1 pt-3 montserrat-font-1">
            <div className="flex justify-between">
              <span>Item Total</span>
              <span>
                ₹ {orderDetails?.item_total || orderDetails?.subtotal || 0}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Sub Total</span>
              <span>₹ {orderDetails?.subtotal}</span>
            </div>
            <div className="flex justify-between">
              <span>Coupon Code Discount</span>
              <span>₹ {orderDetails?.coupon_code_discount || 0}</span>
            </div>
            <div className="flex justify-between">
              <span>Loyalty Point Discount</span>
              <span>
                ₹{" "}
                {orderDetails?.loyalty_points_discount ||
                  orderDetails?.discount_amount ||
                  0}
              </span>
            </div>
            <div className="flex justify-between">
              <span>CGST</span>
              <span>₹ {Number(orderDetails?.tax_cgst || 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span>SGST</span>
              <span>₹ {Number(orderDetails?.tax_sgst || 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span>Delivery Fee</span>
              <span>₹ {orderDetails?.delivery_fee || 0}</span>
            </div>
            <div className="flex justify-between">
              <span>Packaging Fee</span>
              <span>
                ₹ {Number(orderDetails?.packaging_charges || 0).toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between font-semibold text-blue-600 pt-2 border-t mt-1">
              <span>Total Price</span>
              <span>₹ {Number(orderDetails?.pay_amount || 0).toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* RIGHT — Address + Platform Charges */}
        <div className="col-span-1 flex flex-col gap-4 bg-blue-50 montserrat-font-1">
          {/* DINING / ADDRESS CARD */}
          <div className="bg-white rounded-lg p-4 text-sm text-center">
            {isTableBasedOrder ? (
              <>
                <p className="text-gray-500">
                  {normalizedOrderType === "office_order"
                    ? "Office Order"
                    : normalizedOrderType === "car_order"
                      ? "Car Order"
                      : "Dining Order"}
                </p>

                <p className="text-xl font-semibold">
                  Table No.{" "}
                  {orderDetails?.table_no || orderDetails?.tableNo || "-"}
                </p>
              </>
            ) : (
              <>
                <h4 className="font-semibold mb-2">Delivery Address</h4>
                {renderAddress()}
              </>
            )}

            {/* Event details should appear only once, beside address/order details */}
            {renderEventDetails(orderEventDetails)}
          </div>

          {/* PLATFORM CHARGES CARD */}
          <div className="bg-white rounded-lg p-4 text-sm">
            <h4 className="font-semibold mb-2 text-blue-600">
              Platform Charges
            </h4>

            <div className="flex justify-between">
              <span>Messages Sent</span>
              <span>{orderDetails?.message_count || 0}</span>
            </div>

            <div className="flex justify-between">
              <span>Per Message Cost</span>
              <span>
                ₹ {Number(orderDetails?.per_message_cost || 0).toFixed(2)}
              </span>
            </div>

            <div className="flex justify-between">
              <span>Message Charges</span>
              <span>
                ₹ {Number(orderDetails?.message_charges || 0).toFixed(2)}
              </span>
            </div>

            <div className="flex justify-between">
              <span>GST on Message Charges</span>
              <span>
                ₹{" "}
                {Number(orderDetails?.message_charges_tax_gst || 0).toFixed(2)}
              </span>
            </div>

            <div className="flex justify-between font-semibold pt-2 border-t mt-1">
              <span>Total Platform Fee</span>
              <span>
                ₹{" "}
                {Number(orderDetails?.message_charges_tax_gst || 0).toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderTable;
